import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import useGetChainInfo from '@/hooks/useGetChainInfo';
import { getAddressScannerUrl, maskAddressValue } from '@/utils/format';

import { EventTriggerType, Trigger } from '../../domain';
import { TriggerExternalLinkDetail } from './components/TriggerExternalLinkDetail';
import { TriggerType } from './components/TriggerType';

interface Props {
  trigger: Trigger;
  triggerType: EventTriggerType;
}

export const EulerV2LoopingTriggerDetails = ({ trigger, triggerType }: Props) => {
  const { getChainIconUrl } = useGetChainInfo();

  const depositVaultAddress = trigger.context?.depositVault ?? '';
  const borrowVaultAddress = trigger.context?.borrowVault ?? '';
  const chainId = trigger.context?.chain_id ?? 1;

  const networkInfo = useMemo(() => {
    return NETWORKS.find((network) => network.chainId === chainId) ?? null;
  }, [chainId]);

  const chainIconUrl = useMemo(() => {
    if (!networkInfo) return '';
    return getChainIconUrl(networkInfo.chainId) || '';
  }, [networkInfo, getChainIconUrl]);

  const depositScannerUrl = useMemo(() => {
    if (!depositVaultAddress || !networkInfo) return '';
    return getAddressScannerUrl('chainId', networkInfo.chainId, depositVaultAddress) || '';
  }, [depositVaultAddress, networkInfo]);

  const borrowScannerUrl = useMemo(() => {
    if (!borrowVaultAddress || !networkInfo) return '';
    return getAddressScannerUrl('chainId', networkInfo.chainId, borrowVaultAddress) || '';
  }, [borrowVaultAddress, networkInfo]);

  return (
    <div className="tw:flex tw:flex-row tw:gap-6">
      <TriggerType eventType={triggerType} />

      <TriggerExternalLinkDetail
        label="Deposit Vault"
        value={
          <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
            {chainIconUrl && <Image src={chainIconUrl} width={12} height={12} alt={networkInfo?.name || 'chain'} />}
            <span>{maskAddressValue(depositVaultAddress) ?? ''}</span>
          </div>
        }
        href={depositScannerUrl}
      />

      <TriggerExternalLinkDetail
        label="Borrow Vault"
        value={
          <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
            {chainIconUrl && <Image src={chainIconUrl} width={12} height={12} alt={networkInfo?.name || 'chain'} />}
            <span>{maskAddressValue(borrowVaultAddress) ?? ''}</span>
          </div>
        }
        href={borrowScannerUrl}
      />
    </div>
  );
};
